import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import axios from 'axios';
import { XLg, CheckLg } from 'react-bootstrap-icons';
import CarImagesManager from './CarImagesManager';
import ConfirmModal from '../../../common/ui/ConfirmModal';
import notify, { apiFieldErrors, currentLanguage } from '../../../common/ui/notify';
import language from '../../../data/language';
import { toImageArray } from '../../../common/format';

const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;

const EMPTY_CAR = {
    mark: "", model: "", vin: "", year: "", price: "", currency: "USD", phone: "",
    location: "", category: "", millage: "", fuel: "", engine: "", cilinder: "",
    transmission: "", drive: "", doors: "", airbags: "", color: "", lot: "",
    auction: "", description: "", condition_status: "none", inspected: "0"
};

// Labels for the status fields (kept inline so we don't touch the shared
// language bundle). geo / en / ru / am.
const STATUS_LABELS = {
    section: ["სტატუსი", "Status", "Статус", "Կարգավիճակ"],
    condition: ["მდგომარეობა", "Condition", "Состояние", "Վիճակ"],
    none: ["არ არის მითითებული", "Not set", "Не указано", "Նշված չէ"],
    damaged: ["დაზიანებული", "Damaged", "Повреждён", "Վնասված"],
    good: ["კარგ მდგომარეობაში", "Good condition", "В хорошем состоянии", "Լավ վիճակում"],
    inspected: ["შემოწმებული ქარექსპრესის მიერ", "Inspected by Carexpress", "Проверено Carexpress", "Ստուգված է Carexpress-ի կողմից"],
};
const LANG_IDX = { geo: 0, en: 1, ru: 2, am: 3 };

// Kept in sync with what the market filters expect.
const OPTIONS = {
    currency: ["USD", "GEL", "EUR"],
    fuel: ["Gasoline", "Diesel", "Hybrid", "Electric", "Gas"],
    transmission: ["Automatic", "Manual", "CVT", "Tiptronic"],
    drive: ["Front Wheels Drive", "Rear Wheels Drive", "All Wheels Drive"],
    category: ["sedan", "jeep", "coupe", "hatchback", "minivan", "micro-bus", "pickup", "universal", "motorcycle", "quadrocycle"],
    auction: ["Copart", "IAAI", "Manheim", "Other"],
    location: [
        "რუსთავი, ქარექსპრესის ტერმინალი",
        "თბილისი, გ. ფანჯიკიძეს ქუჩა 22",
        "ქუთაისი, პუშკინის ქუჩა 2",
        "ფოთი, კოკაიას ხეივანი 1"
    ]
};

// Which section each field belongs to, so a validation error can send the user
// to the right tab instead of just failing silently.
const FIELD_SECTION = {
    mark: 'basic', model: 'basic', vin: 'basic', year: 'basic', lot: 'basic', auction: 'basic',
    millage: 'technical', fuel: 'technical', engine: 'technical', cilinder: 'technical',
    transmission: 'technical', drive: 'technical', doors: 'technical', airbags: 'technical', color: 'technical',
    condition_status: 'technical', inspected: 'technical',
    price: 'price', currency: 'price', phone: 'price', location: 'price', category: 'price',
    description: 'description'
};

const REQUIRED_FIELDS = ['mark', 'model', 'year', 'price', 'phone', 'location'];

const CarForm = ({show, car, onClose, onSaved}) => {
    const lang = currentLanguage();
    const strings = language[lang].carForm;
    const adminStrings = language[lang].admin;
    const common = language[lang].common;

    const isEdit = Boolean(car && car.id);

    const [section, setSection] = useState('basic');
    const [form, setForm] = useState(EMPTY_CAR);
    const [features, setFeatures] = useState([]);
    const [featureDraft, setFeatureDraft] = useState("");
    const [images, setImages] = useState([]);
    const [carId, setCarId] = useState(null);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [decoding, setDecoding] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);

    const savedRef = useRef(false);

    // Load the record being edited (or reset for a new one) whenever the modal opens.
    useEffect(() => {
        if (!show) return;
        // "Manage photos" from the table opens straight on the gallery tab.
        setSection(car && car.id && car.__startOnImages ? 'images' : 'basic');
        setErrors({});
        setFeatureDraft("");
        setDirty(false);
        savedRef.current = false;

        if (car && car.id) {
            const next = {...EMPTY_CAR};
            Object.keys(EMPTY_CAR).forEach((key) => {
                next[key] = car[key] === null || car[key] === undefined ? "" : String(car[key]);
            });
            next.currency = car.currency || "USD";
            setForm(next);
            setFeatures(Array.isArray(car.features) ? car.features : []);
            setImages(toImageArray(car.images));
            setCarId(car.id);
        } else {
            setForm(EMPTY_CAR);
            setFeatures([]);
            setImages([]);
            setCarId(null);
        }
    }, [show, car]);

    // Warn before a browser-level navigation away from unsaved work.
    useEffect(() => {
        if (!show || !dirty) return undefined;
        const handler = (event) => {
            event.preventDefault();
            event.returnValue = "";
            return "";
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [show, dirty]);

    const update = (field) => (event) => {
        const {value} = event.target;
        setForm((prev) => ({...prev, [field]: value}));
        setDirty(true);
        setErrors((prev) => (prev[field] ? {...prev, [field]: undefined} : prev));
    };

    /** Client-side checks mirroring the server rules, so errors surface instantly. */
    const validate = () => {
        const next = {};

        REQUIRED_FIELDS.forEach((field) => {
            if (!String(form[field] || "").trim()) next[field] = common.required_note;
        });

        if (form.vin && !VIN_PATTERN.test(form.vin.toUpperCase())) {
            next.vin = strings.vin_hint;
        }

        const year = Number(form.year);
        if (form.year && (!Number.isInteger(year) || year < 1900 || year > new Date().getFullYear() + 1)) {
            next.year = strings.year;
        }

        ['price', 'millage', 'cilinder', 'doors', 'airbags'].forEach((field) => {
            if (!form[field]) return;
            const numeric = Number(String(form[field]).replace(/[^\d.]/g, ""));
            if (!Number.isFinite(numeric) || numeric < 0) next[field] = strings[field] || common.error_title;
        });

        return next;
    };

    const decodeVin = async () => {
        const vin = String(form.vin || "").trim().toUpperCase();
        if (!VIN_PATTERN.test(vin)) {
            setErrors((prev) => ({...prev, vin: strings.vin_hint}));
            return;
        }

        setDecoding(true);
        try {
            const res = await axios.get(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/${vin}?format=json`);
            const result = (res.data && res.data.Results && res.data.Results[0]) || null;
            if (!result || (!result.Make && !result.Model)) {
                notify.warning(strings.vin_decode_failed);
                return;
            }

            // Only fill what came back — never blank out something already typed.
            setForm((prev) => ({
                ...prev,
                mark: result.Make || prev.mark,
                model: result.Model || prev.model,
                year: result.ModelYear || prev.year,
                doors: result.Doors || prev.doors,
                cilinder: result.EngineCylinders || prev.cilinder,
                fuel: result.FuelTypeSecondary === "Electric"
                    ? "Hybrid"
                    : (result.FuelTypePrimary ? String(result.FuelTypePrimary).split(" ")[0] : prev.fuel)
            }));
            setDirty(true);
            notify.success(strings.vin_decoded);
        } catch (error) {
            notify.warning(strings.vin_decode_failed);
        } finally {
            setDecoding(false);
        }
    };

    const addFeature = () => {
        const value = featureDraft.trim();
        if (!value) return;
        if (features.includes(value)) {
            setFeatureDraft("");
            return;
        }
        setFeatures((prev) => [...prev, value]);
        setFeatureDraft("");
        setDirty(true);
    };

    const save = async (event) => {
        if (event) event.preventDefault();
        if (saving) return; // duplicate submit guard

        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            const firstField = Object.keys(validationErrors)[0];
            setSection(FIELD_SECTION[firstField] || 'basic');
            notify.error(strings.fix_errors);
            return;
        }

        setSaving(true);
        setErrors({});

        const payload = {...form, features};

        try {
            const res = carId
                ? await axios.put(`/api/cars/${carId}`, payload)
                : await axios.post('/api/cars', payload);

            const saved = res.data.data;
            const newId = carId || res.data.id;

            setCarId(newId);
            setImages(toImageArray(saved && saved.images));
            setDirty(false);
            savedRef.current = true;

            notify.success(carId ? adminStrings.car_updated : adminStrings.car_created);
            onSaved(saved);

            // A brand new car has no gallery yet — take the user straight there.
            if (!carId) setSection('images');
        } catch (error) {
            const fieldErrors = apiFieldErrors(error);
            if (Object.keys(fieldErrors).length > 0) {
                setErrors(fieldErrors);
                const firstField = Object.keys(fieldErrors)[0];
                setSection(FIELD_SECTION[firstField] || 'basic');
            }
            notify.apiError(error);
        } finally {
            setSaving(false);
        }
    };

    const requestClose = () => {
        if (saving) return;
        if (dirty) {
            setConfirmClose(true);
            return;
        }
        onClose(savedRef.current);
    };

    const sections = useMemo(() => ([
        {key: 'basic', label: strings.step_basic},
        {key: 'technical', label: strings.step_technical},
        {key: 'price', label: strings.step_price},
        {key: 'description', label: strings.step_description},
        {key: 'images', label: strings.step_images, disabled: !carId}
    ]), [strings, carId]);

    const field = (name, label, {type = "text", required = false, options = null, hint = null, colSpan = 1} = {}) => (
        <div className={`cx-field admin-form-field${colSpan > 1 ? " admin-form-field-wide" : ""}`}>
            <label className="cx-label" htmlFor={`car-${name}`}>
                {label}{required && <span className="cx-required" aria-hidden="true">*</span>}
            </label>
            {options ? (
                <select
                    id={`car-${name}`}
                    className={`cx-select ${errors[name] ? "cx-select-invalid" : ""}`}
                    value={form[name]}
                    onChange={update(name)}
                    aria-invalid={Boolean(errors[name])}
                >
                    <option value="">{strings.select_placeholder}</option>
                    {/* An existing value that is not in the list stays selectable. */}
                    {form[name] && !options.includes(form[name]) && <option value={form[name]}>{form[name]}</option>}
                    {options.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
            ) : (
                <input
                    id={`car-${name}`}
                    className={`cx-input ${errors[name] ? "cx-input-invalid" : ""}`}
                    type={type}
                    inputMode={type === "number" ? "numeric" : undefined}
                    value={form[name]}
                    onChange={update(name)}
                    aria-invalid={Boolean(errors[name])}
                />
            )}
            {errors[name] && <p className="cx-error-text">{errors[name]}</p>}
            {!errors[name] && hint && <p className="cx-hint">{hint}</p>}
        </div>
    );

    return (
        <>
            <Modal show={show} size="xl" backdrop="static" onHide={requestClose} scrollable contentClassName="cx-dark-modal">
                <Modal.Header>
                    <Modal.Title>{isEdit || carId ? adminStrings.edit_car : adminStrings.add_car}</Modal.Title>
                    <button type="button" className="admin-modal-close" aria-label={common.close} onClick={requestClose}>
                        <XLg aria-hidden="true" />
                    </button>
                </Modal.Header>

                <Modal.Body className="admin-modal-body">
                    <nav className="admin-form-tabs" role="tablist">
                        {sections.map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                role="tab"
                                aria-selected={section === item.key}
                                disabled={item.disabled}
                                title={item.disabled ? strings.save_car : undefined}
                                className={`admin-form-tab ${section === item.key ? "is-active" : ""}`}
                                onClick={() => setSection(item.key)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </nav>

                    <form id="car-form" className="admin-form" onSubmit={save} noValidate>
                        {section === 'basic' && (
                            <div className="admin-form-grid">
                                <div className="cx-field admin-form-field admin-form-field-wide">
                                    <label className="cx-label" htmlFor="car-vin">{strings.vin}</label>
                                    <div className="admin-vin-row">
                                        <input
                                            id="car-vin"
                                            className={`cx-input ${errors.vin ? "cx-input-invalid" : ""}`}
                                            value={form.vin}
                                            maxLength={17}
                                            onChange={(e) => {
                                                setForm((prev) => ({...prev, vin: e.target.value.toUpperCase()}));
                                                setDirty(true);
                                                setErrors((prev) => (prev.vin ? {...prev, vin: undefined} : prev));
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    decodeVin();
                                                }
                                            }}
                                            aria-invalid={Boolean(errors.vin)}
                                        />
                                        <button type="button" className="cx-btn cx-btn-subtle" disabled={decoding} onClick={decodeVin}>
                                            {decoding
                                                ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                : strings.decode_vin}
                                        </button>
                                    </div>
                                    {errors.vin ? <p className="cx-error-text">{errors.vin}</p> : <p className="cx-hint">{strings.vin_hint}</p>}
                                </div>

                                {field('mark', strings.mark, {required: true})}
                                {field('model', strings.model, {required: true})}
                                {field('year', strings.year, {type: "number", required: true})}
                                {field('lot', strings.lot)}
                                {field('auction', strings.auction, {options: OPTIONS.auction})}
                            </div>
                        )}

                        {section === 'technical' && (
                            <div className="admin-form-grid">
                                {field('millage', strings.millage, {type: "number", required: true})}
                                {field('fuel', strings.fuel, {options: OPTIONS.fuel, required: true})}
                                {field('engine', strings.engine, {required: true})}
                                {field('cilinder', strings.cilinder, {type: "number", required: true})}
                                {field('transmission', strings.transmission, {options: OPTIONS.transmission, required: true})}
                                {field('drive', strings.drive, {options: OPTIONS.drive, required: true})}
                                {field('doors', strings.doors, {type: "number", required: true})}
                                {field('airbags', strings.airbags, {type: "number", required: true})}
                                {field('color', strings.color, {required: true})}

                                <div className="cx-field admin-form-field">
                                    <label className="cx-label" htmlFor="car-condition_status">{STATUS_LABELS.condition[LANG_IDX[lang] ?? 0]}</label>
                                    <select
                                        id="car-condition_status"
                                        className="cx-select"
                                        value={form.condition_status || "none"}
                                        onChange={update('condition_status')}
                                    >
                                        <option value="none">{STATUS_LABELS.none[LANG_IDX[lang] ?? 0]}</option>
                                        <option value="damaged">{STATUS_LABELS.damaged[LANG_IDX[lang] ?? 0]}</option>
                                        <option value="good">{STATUS_LABELS.good[LANG_IDX[lang] ?? 0]}</option>
                                    </select>
                                </div>

                                <div className="cx-field admin-form-field admin-form-field-wide">
                                    <label style={{ display: "inline-flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
                                        <input
                                            type="checkbox"
                                            style={{ width: "18px", height: "18px", flex: "none" }}
                                            checked={String(form.inspected) === "1"}
                                            onChange={(e) => { setForm((prev) => ({...prev, inspected: e.target.checked ? "1" : "0"})); setDirty(true); }}
                                        />
                                        <span className="cx-label" style={{ margin: 0 }}>{STATUS_LABELS.inspected[LANG_IDX[lang] ?? 0]}</span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {section === 'price' && (
                            <div className="admin-form-grid">
                                {field('price', strings.price, {type: "number", required: true})}
                                {field('currency', strings.currency, {options: OPTIONS.currency, required: true})}
                                {field('phone', strings.phone, {type: "tel", required: true})}
                                {field('location', strings.location, {required: true})}
                                {field('category', strings.category, {options: OPTIONS.category, required: true})}
                            </div>
                        )}

                        {section === 'description' && (
                            <div className="admin-form-grid">
                                <div className="cx-field admin-form-field admin-form-field-full">
                                    <label className="cx-label" htmlFor="car-description">{strings.description}</label>
                                    <textarea
                                        id="car-description"
                                        className="cx-textarea"
                                        rows={6}
                                        placeholder={strings.description_placeholder}
                                        value={form.description}
                                        onChange={update('description')}
                                    />
                                </div>

                                <div className="cx-field admin-form-field admin-form-field-full">
                                    <label className="cx-label" htmlFor="car-feature-input">{strings.features}</label>
                                    <div className="admin-vin-row">
                                        <input
                                            id="car-feature-input"
                                            className="cx-input"
                                            placeholder={strings.features_placeholder}
                                            value={featureDraft}
                                            onChange={(e) => setFeatureDraft(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addFeature();
                                                }
                                            }}
                                        />
                                        <button type="button" className="cx-btn cx-btn-subtle" onClick={addFeature}>
                                            {strings.add_feature}
                                        </button>
                                    </div>

                                    {features.length > 0 && (
                                        <ul className="admin-feature-list">
                                            {features.map((feature) => (
                                                <li key={feature} className="admin-feature">
                                                    {feature}
                                                    <button
                                                        type="button"
                                                        aria-label={`${common.delete}: ${feature}`}
                                                        onClick={() => {
                                                            setFeatures((prev) => prev.filter((item) => item !== feature));
                                                            setDirty(true);
                                                        }}
                                                    >
                                                        <XLg aria-hidden="true" />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        )}

                        {section === 'images' && carId && (
                            <CarImagesManager
                                carId={carId}
                                images={images}
                                onChange={(nextImages) => {
                                    setImages(nextImages);
                                    onSaved({...(car || {}), id: carId, images: nextImages});
                                }}
                            />
                        )}
                    </form>
                </Modal.Body>

                <Modal.Footer className="admin-modal-footer">
                    <p className="admin-form-note">{common.required_note}</p>
                    <div className="admin-modal-actions">
                        <button type="button" className="cx-btn cx-btn-ghost" disabled={saving} onClick={requestClose}>
                            {section === 'images' ? strings.finish : common.cancel}
                        </button>
                        {section !== 'images' && (
                            <button type="submit" form="car-form" className="cx-btn cx-btn-primary" disabled={saving}>
                                {saving
                                    ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> {common.saving}</>
                                    : <><CheckLg aria-hidden="true" /> {carId ? common.save : strings.save_and_continue}</>}
                            </button>
                        )}
                    </div>
                </Modal.Footer>
            </Modal>

            <ConfirmModal
                show={confirmClose}
                tone="question"
                title={common.confirm_title}
                message={common.unsaved_changes}
                confirmLabel={common.yes}
                cancelLabel={common.no}
                onConfirm={() => {
                    setConfirmClose(false);
                    setDirty(false);
                    onClose(savedRef.current);
                }}
                onCancel={() => setConfirmClose(false)}
            />
        </>
    );
};

export default CarForm;
